import { displayName } from '@common/helpers/userDisplay'
import { MentionType } from '@common/interfaces/mentions'
import { ITeam, ITeamMember } from '@common/interfaces/team'
import { userRoleGroups } from '@common/interfaces/user'
import { IMentionSuggestion } from '../../../components/Common/Mentions'

type INamedTeamMember = ITeamMember & { user: NonNullable<ITeamMember['user']> }

/** Matches `loadActiveTeamMembers`: a role change after joining doesn't make someone messageable. */
const isMessageableMember = (member: ITeamMember): member is INamedTeamMember =>
  !!member.user?.role && userRoleGroups.MESSAGING.includes(member.user.role)

/**
 * Replaces each Team in a picker's next value with its members as person chips,
 * so only user ids ever reach the DM services. A team whose roster isn't loaded
 * adds nobody, and a member already picked, excluded or shared by two teams is
 * added once at most.
 */
export const expandTeamSelections = (
  next: IMentionSuggestion[],
  teams: ITeam[],
  excludeUserIds: string[] = [],
): IMentionSuggestion[] => {
  const people = next.filter((option) => option.type !== MentionType.Team)
  const alreadyChosen = new Set([...excludeUserIds, ...people.map((option) => option.id)])
  const teamsById = new Map(teams.map((team) => [team.id, team]))

  const newMembers = next
    .filter((option) => option.type === MentionType.Team)
    .flatMap((option) => teamsById.get(option.id)?.members ?? [])
    .filter(isMessageableMember)
    .filter((member) => !alreadyChosen.has(member.userId))
  const uniqueMembers = new Map(newMembers.map((member) => [member.userId, member]))

  return [
    ...people,
    ...[...uniqueMembers.values()].map(({ userId, user }) => ({
      id: userId,
      name: displayName(user),
      type: MentionType.InternalUser,
    })),
  ]
}
